import { useEffect, useMemo, useRef } from 'react';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { runAlexandriaQuery } from 'dashboard409a/services/alexandria/api';
import {
  Dashboard409ChartMetricSeriesDocument,
  Dashboard409SummaryMetricSeriesDocument,
} from 'dashboard409a/services/alexandria/generated/graphql';
import { MetricSeriesResult } from 'dashboard409a/services/alexandria/types';
import { clientWithAuth } from 'dashboard409a/services/base';
import { getGpcCompSet } from 'dashboard409a/services/endpoints/dashboard';
import { buildCompsSeriesResult, CompsSeriesResult } from './compute/combineResults';
import { CHART_METRICS, CompRef, COMPS_METRICS, DEFAULT_RANGE_KEY, TIME_RANGES } from './constants';

// Alexandria refreshes once a day, so within a session cached windows stay
// valid — range flips back to a visited window must not refetch.
const ONE_HOUR_MS = 60 * 60 * 1000;
const COMP_SET_REQUEST_TIMEOUT_MS = 15_000;

// Keep the query shape explicit in the cache identity.
const COMP_SERIES_CACHE_SCHEMA = 'schema-v1';
export const COMP_SERIES_QUERY_KEY = ['alexandria', 'comp-series-batch', COMP_SERIES_CACHE_SCHEMA] as const;

const ISO_DATE = 'YYYY-MM-DD';
const YTD_DAILY_DAYS = 182;
// The compact response was measured safely at roughly this many points. If a
// valuation has more comps, increase the interval rather than risking the
// AppSync/Lambda response-size ceilings.
const MAX_ESTIMATED_SERIES_POINTS = 26_500;

const ytdIntervalDays = (daysInRange: number): number => (daysInRange <= YTD_DAILY_DAYS ? 1 : 2);

// Use the viewer's local calendar and end at the previous local day. This
// avoids requesting the current day before Alexandria's daily ingestion is
// complete and presenting a carried-forward value as a new observation.
export const rangeToDates = (rangeKey: string): { startDate: string; endDate: string; intervalDays: number } => {
  const range = TIME_RANGES.find(r => r.key === rangeKey) ?? TIME_RANGES[0];
  const end = moment().subtract(1, 'days').startOf('day');
  const requestedStart = range.key === 'ytd' ? moment().startOf('year') : end.clone().subtract(range.days - 1, 'days');
  // On January 1 there are no completed days in the new year. Use the
  // previous day as a one-point range instead of sending start > end.
  const start = requestedStart.isAfter(end) ? end.clone() : requestedStart;
  const daysInRange = end.diff(start, 'days') + 1;
  return {
    startDate: start.format(ISO_DATE),
    endDate: end.format(ISO_DATE),
    intervalDays: range.key === 'ytd' ? ytdIntervalDays(daysInRange) : range.intervalDays,
  };
};

export interface CompSetEntry {
  company_name: string | null;
  ticker_symbol: string | null;
  cap_iq_id: string | null;
}

// The comparable set configured for the measurement date (GPCComparison).
// Identity only — metric data comes from Alexandria. Deliberately uncached
// (staleTime 0): comps are edited in the valuation screens and by model
// uploads, which never touch this cache, and the request is one tiny query
// to our own backend. Fetching it fresh on every mount is what lets the
// heavy ticker-keyed Alexandria cache self-correct when the set changes.
export const useCompSet = (cmdId: number | null | undefined) =>
  useQuery({
    queryKey: ['gpc-comp-set', 'v2', cmdId],
    enabled: !!cmdId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      const { data } = await clientWithAuth.get<CompSetEntry[]>(getGpcCompSet(cmdId as number), {
        signal,
        timeout: COMP_SET_REQUEST_TIMEOUT_MS,
      });
      return data;
    },
  });

export const compSetToRefs = (entries: CompSetEntry[] | undefined): CompRef[] =>
  (entries ?? []).flatMap(entry => {
    if (entry.ticker_symbol) {
      return [{ ticker: entry.ticker_symbol, label: entry.company_name || entry.ticker_symbol }];
    }
    return [];
  });

export const compSetMissingTickerLabels = (entries: CompSetEntry[] | undefined): string[] =>
  (entries ?? [])
    .filter(entry => !entry.ticker_symbol)
    .map(entry => entry.company_name || entry.cap_iq_id || 'Unknown comparable company');

export const payloadSafeIntervalDays = (
  startDate: string,
  endDate: string,
  requestedIntervalDays: number,
  companyCount: number,
  metricCount: number
): number => {
  const daysInRange = moment(endDate, ISO_DATE).diff(moment(startDate, ISO_DATE), 'days') + 1;
  let intervalDays = requestedIntervalDays;
  // The end date is appended when the normal interval grid misses it, so this
  // is the exact maximum number of requested dates for a given interval.
  const estimatedPoints = (): number => (Math.ceil((daysInRange - 1) / intervalDays) + 1) * companyCount * metricCount;
  while (estimatedPoints() > MAX_ESTIMATED_SERIES_POINTS) {
    intervalDays += 1;
  }
  return intervalDays;
};

// 'full' includes the once-per-company metadata used by the container widgets;
// 'chart' omits it because range charts already have labels from the comp set.
export type SeriesVariant = 'full' | 'chart';

// Alexandria nests observations under each company. Flatten them into one list,
// tagging every point with its company's requested_identifier, so the compute
// layer keeps consuming a single flat point stream.
const flattenMetricSeries = (result: {
  companies: ReadonlyArray<{
    requested_identifier: string;
    company_name?: string | null;
    data_points: ReadonlyArray<{
      metric_key: string;
      requested_date: string;
      value: string | null;
      as_of_date?: string | null;
    }>;
  }>;
  unresolved_tickers: ReadonlyArray<string>;
}): MetricSeriesResult => ({
  companies: result.companies.map(company => ({
    requested_identifier: company.requested_identifier,
    company_name: company.company_name,
  })),
  points: result.companies.flatMap(company =>
    company.data_points.map(point => ({ ...point, requested_identifier: company.requested_identifier }))
  ),
  unresolved_tickers: [...result.unresolved_tickers],
});

const sameStringList = (left: unknown, right: unknown): boolean =>
  Array.isArray(left) &&
  Array.isArray(right) &&
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

// Range switches may keep their previous response visible, but a report-date
// change can replace the comp set. Never carry data across different company
// identities or the new report can briefly display the previous report's data.
export const previousDataForSameComps = <T>(
  previousData: T | undefined,
  previousQueryKey: readonly unknown[] | undefined,
  currentQueryKey: readonly unknown[]
): T | undefined => {
  if (!previousQueryKey) {
    return undefined;
  }
  return sameStringList(previousQueryKey[4], currentQueryKey[4]) ? previousData : undefined;
};

// Single source of truth for a comp-series request: the container, the
// charts, and the background prefetcher must all build their options here —
// a hand-built key that drifts from this one silently double-fetches.
export const compSeriesOptions = (comps: CompRef[], rangeKey: string, metrics: string[], variant: SeriesVariant) => {
  const { startDate, endDate, intervalDays: requestedIntervalDays } = rangeToDates(rangeKey);
  // The backend does not guarantee comparable-company ordering. Normalize
  // identifiers so an order-only change does not create a new cache entry.
  const tickers = comps.map(comp => comp.ticker).sort();
  const intervalDays = payloadSafeIntervalDays(startDate, endDate, requestedIntervalDays, comps.length, metrics.length);

  return queryOptions({
    queryKey: [...COMP_SERIES_QUERY_KEY, variant, tickers, metrics, startDate, endDate, intervalDays],
    staleTime: ONE_HOUR_MS,
    gcTime: ONE_HOUR_MS,
    // Range responses can be several megabytes. Keep them fast within the
    // current tab without copying them into the app-wide IndexedDB cache.
    meta: { persist: false },
    queryFn: async ({ signal }): Promise<MetricSeriesResult> => {
      // Alexandria resolves both tickers and direct company IDs through the
      // single required `tickers` argument (`requested_identifier`).
      const variables = {
        tickers,
        metrics,
        start_date: startDate,
        end_date: endDate,
        interval_days: intervalDays,
      };
      const data =
        variant === 'chart'
          ? await runAlexandriaQuery(Dashboard409ChartMetricSeriesDocument, variables, { signal })
          : await runAlexandriaQuery(Dashboard409SummaryMetricSeriesDocument, variables, { signal });
      return flattenMetricSeries(data.metric_series);
    },
  });
};

// One batched request per comp set. The service boundary restores the parent
// company's requested_identifier onto each point for the existing consumers.
export const useCompsSeries = (
  comps: CompRef[],
  rangeKey: string,
  metrics: string[] = COMPS_METRICS,
  variant: SeriesVariant = 'full'
): CompsSeriesResult => {
  const options = compSeriesOptions(comps, rangeKey, metrics, variant);
  const query = useQuery({
    ...options,
    enabled: comps.length > 0,
    refetchOnWindowFocus: false,
    // Keep the previous range visible only while the comp identity is stable.
    placeholderData: (previousData, previousQuery) =>
      previousDataForSameComps(previousData, previousQuery?.queryKey, options.queryKey),
  });

  return useMemo(
    () =>
      buildCompsSeriesResult(comps, query.data, {
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        dataUpdatedAt: query.dataUpdatedAt,
      }),
    [comps, query.data, query.isLoading, query.isFetching, query.isError, query.dataUpdatedAt]
  );
};

// Range charts. At the default range this resolves to the container's own
// full entry (CHART_METRICS is a subset of COMPS_METRICS), so the initial
// view costs one metric-series request instead of two.
export const useChartSeries = (comps: CompRef[], rangeKey: string): CompsSeriesResult =>
  useCompsSeries(
    comps,
    rangeKey,
    rangeKey === DEFAULT_RANGE_KEY ? COMPS_METRICS : CHART_METRICS,
    rangeKey === DEFAULT_RANGE_KEY ? 'full' : 'chart'
  );

// Every range switch costs ~4s on a cache miss, so once the initial view has
// data, warm the remaining ranges in the background. Sequential on purpose:
// it must never compete with an interactive fetch for bandwidth.
const PREFETCH_ORDER = ['month_6', 'ytd', 'year_1', 'year_3', 'year_5', 'year_10'];

export const usePrefetchCompRanges = (comps: CompRef[], enabled: boolean): void => {
  const queryClient = useQueryClient();
  const startedFor = useRef<string | null>(null);
  const { endDate } = rangeToDates(DEFAULT_RANGE_KEY);
  const identity = `${comps.map(comp => comp.ticker).join('|')}::${endDate}`;

  useEffect(() => {
    if (!enabled || comps.length === 0 || startedFor.current === identity) {
      return;
    }
    startedFor.current = identity;

    let cancelled = false;
    // One range in flight at a time keeps bandwidth free for interactive
    // fetches. Stop scheduling the old comp set when report identity changes.
    PREFETCH_ORDER.reduce(
      (chain, rangeKey) =>
        chain.then(() => {
          if (cancelled) {
            return undefined;
          }
          return queryClient.prefetchQuery(compSeriesOptions(comps, rangeKey, CHART_METRICS, 'chart'));
        }),
      Promise.resolve() as Promise<unknown>
    );

    return () => {
      cancelled = true;
    };
  }, [enabled, identity, comps, queryClient]);
};
